import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Calendar, MapPin, Star, CheckCircle, XCircle,
    MessageCircle, Phone
} from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'

export default function BookingsPage() {
    useTranslation()
    const { language } = useLanguageStore()
    useCurrencyStore()

    const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all')

    // Mock data
    const bookings = [
        {
            id: 1,
            vehicle: { id: 1, title: 'Honda Air Blade 160', type: 'motorcycle', image: '/images/vehicles/airblade.jpg' },
            owner: { name: 'Nguyễn Văn Minh', phone: '+84912345678', rating: 4.9 },
            startDate: '2026-01-08',
            endDate: '2026-01-10',
            pickupLocation: 'Dương Đông, Phú Quốc',
            totalAmount: 400000,
            status: 'confirmed',
            paymentStatus: 'paid',
            createdAt: '2026-01-06',
        },
        {
            id: 2,
            vehicle: { id: 2, title: 'Toyota Vios 2023', type: 'car', image: '/images/vehicles/vios.jpg' },
            owner: { name: 'Trần Thị Hoa', phone: '+84912345679', rating: 4.8 },
            startDate: '2026-01-05',
            endDate: '2026-01-07',
            pickupLocation: 'Sân bay Phú Quốc',
            totalAmount: 1600000,
            status: 'completed',
            paymentStatus: 'released',
            createdAt: '2026-01-03',
            reviewed: false,
        },
        {
            id: 3,
            vehicle: { id: 3, title: 'Honda Wave Alpha', type: 'motorcycle', image: '/images/vehicles/wave.jpg' },
            owner: { name: 'Lê Hoàng Nam', phone: '+84912345680', rating: 4.7 },
            startDate: '2025-12-28',
            endDate: '2025-12-30',
            pickupLocation: 'An Thới, Phú Quốc',
            totalAmount: 300000,
            status: 'completed',
            paymentStatus: 'released',
            createdAt: '2025-12-25',
            reviewed: true,
        },
    ]

    const filteredBookings = filter === 'all'
        ? bookings
        : bookings.filter(b => b.status === filter || (filter === 'active' && b.status === 'confirmed'))

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'confirmed':
                return <span className="badge badge-success">{language === 'en' ? 'Confirmed' : 'Đã xác nhận'}</span>
            case 'pending':
                return <span className="badge badge-warning">{language === 'en' ? 'Pending' : 'Chờ xác nhận'}</span>
            case 'completed':
                return <span className="badge badge-gray">{language === 'en' ? 'Completed' : 'Hoàn thành'}</span>
            case 'cancelled':
                return <span className="badge badge-danger">{language === 'en' ? 'Cancelled' : 'Đã hủy'}</span>
            default:
                return <span className="badge">{status}</span>
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold">{language === 'en' ? 'My Bookings' : 'Đơn Đặt Xe'}</h1>
                    <p className="text-gray-600">{language === 'en' ? 'View and manage your bookings' : 'Xem và quản lý đơn đặt xe'}</p>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto">
                    {(['all', 'active', 'completed', 'cancelled'] as const).map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${filter === status
                                ? 'bg-primary-600 text-white'
                                : 'bg-white hover:bg-gray-100'
                                }`}
                        >
                            {status === 'all' && (language === 'en' ? 'All' : 'Tất cả')}
                            {status === 'active' && (language === 'en' ? 'Active' : 'Đang thuê')}
                            {status === 'completed' && (language === 'en' ? 'Completed' : 'Hoàn thành')}
                            {status === 'cancelled' && (language === 'en' ? 'Cancelled' : 'Đã hủy')}
                        </button>
                    ))}
                </div>

                {/* Bookings List */}
                <div className="space-y-4">
                    {filteredBookings.map((booking) => (
                        <div key={booking.id} className="card hover:shadow-lg transition">
                            <div className="flex flex-col md:flex-row gap-4">
                                {/* Vehicle Image */}
                                <div className="w-full md:w-40 aspect-video md:aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                                    {booking.vehicle.type === 'motorcycle' ? '🏍️' : '🚗'}
                                </div>

                                {/* Booking Details */}
                                <div className="flex-1">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <Link to={`/vehicles/${booking.vehicle.id}`} className="font-bold text-lg hover:text-primary-600">
                                                {booking.vehicle.title}
                                            </Link>
                                            <p className="text-sm text-gray-600">
                                                {language === 'en' ? 'Booking' : 'Mã đơn'} #{booking.id}
                                            </p>
                                        </div>
                                        {getStatusBadge(booking.status)}
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            <span>{booking.startDate} → {booking.endDate}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <MapPin className="w-4 h-4" />
                                            <span className="truncate">{booking.pickupLocation}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Star className="w-4 h-4 text-yellow-500" />
                                            <span>{booking.owner.name} ({booking.owner.rating})</span>
                                        </div>
                                        <div className="font-bold text-primary-600">
                                            {formatCurrency(booking.totalAmount, 'VND')}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                                        {booking.status === 'confirmed' && (
                                            <>
                                                <a href={`tel:${booking.owner.phone}`} className="btn btn-secondary text-sm">
                                                    <Phone className="w-4 h-4 mr-1" />
                                                    {language === 'en' ? 'Call Owner' : 'Gọi chủ xe'}
                                                </a>
                                                <button className="btn btn-secondary text-sm">
                                                    <MessageCircle className="w-4 h-4 mr-1" />
                                                    {language === 'en' ? 'Message' : 'Nhắn tin'}
                                                </button>
                                                <button className="btn text-red-600 border-red-200 hover:bg-red-50 text-sm">
                                                    <XCircle className="w-4 h-4 mr-1" />
                                                    {language === 'en' ? 'Cancel' : 'Hủy đơn'}
                                                </button>
                                            </>
                                        )}
                                        {booking.status === 'completed' && !booking.reviewed && (
                                            <Link to={`/dashboard/bookings/${booking.id}/review`} className="btn btn-primary text-sm">
                                                <Star className="w-4 h-4 mr-1" />
                                                {language === 'en' ? 'Leave Review' : 'Viết đánh giá'}
                                            </Link>
                                        )}
                                        {booking.status === 'completed' && booking.reviewed && (
                                            <span className="text-green-600 text-sm flex items-center">
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                {language === 'en' ? 'Reviewed' : 'Đã đánh giá'}
                                            </span>
                                        )}
                                        <Link to={`/dashboard/bookings/${booking.id}`} className="btn btn-secondary text-sm ml-auto">
                                            {language === 'en' ? 'View Details' : 'Xem chi tiết'}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredBookings.length === 0 && (
                        <div className="card text-center py-12">
                            <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500 mb-4">
                                {language === 'en' ? 'No bookings found' : 'Không có đơn đặt xe'}
                            </p>
                            <Link to="/vehicles" className="btn btn-primary">
                                {language === 'en' ? 'Browse Vehicles' : 'Tìm xe'}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
